
import { supabaseWorker } from '@/integrations/supabase/workerClient';
import { SearchResult } from './types';
import { calculateRelevance, detectQueryType } from './utils';

export const searchInventory = async (query: string, limit: number, userId?: string): Promise<SearchResult[]> => {
  const queryType = detectQueryType(query);

  type Thenable<T> = { then: (onfulfilled: (value: T) => void, onrejected: (reason: unknown) => void) => unknown };
  const wrapThenable = <T>(qb: Thenable<T>): Promise<T> => new Promise((resolve, reject) => qb.then(resolve, reject));

  const baseSelect = (uid?: string) => {
    let qb = supabaseWorker
      .from('inventory')
      .select('id, name, description, status, created_at, daily_price, sku');
    if (uid) {
      // В соответствии с RLS политики, ограничиваем выборку по пользователю
      qb = qb.eq('user_id', uid);
    }
    return qb;
  };

  type DbInventoryRow = {
    id: string;
    name: string;
    description: string | null;
    status: string | null;
    created_at: string;
    daily_price: number | null;
    sku: string | null;
  };
  // Используем unknown для совместимости с FetchQueryBuilder, который возвращает unknown
  let promises: Array<Promise<{ data: unknown | null; error: unknown | null }>> = [];

  if (queryType === 'id') {
    promises = [
      wrapThenable(baseSelect(userId).eq('id', query).limit(limit)),
      wrapThenable(baseSelect(userId).ilike('id', `${query}%`).limit(limit)),
      wrapThenable(baseSelect(userId).ilike('sku', `%${query}%`).limit(limit)),
      wrapThenable(baseSelect(userId).ilike('name', `%${query}%`).limit(limit)),
    ];
  } else {
    promises = [
      wrapThenable(baseSelect(userId).ilike('name', `%${query}%`).limit(limit)),
      wrapThenable(baseSelect(userId).ilike('description', `%${query}%`).limit(limit)),
      wrapThenable(baseSelect(userId).ilike('sku', `%${query}%`).limit(limit)),
    ];
  }

  const settled = await Promise.allSettled(promises);
  let hadRejection = false;
  const rows = settled.flatMap(s => {
    if (s.status === 'fulfilled') {
      const r = s.value as { data: unknown | null };
      return Array.isArray(r?.data) ? (r.data as DbInventoryRow[]) : [];
    } else {
      hadRejection = true;
      // eslint-disable-next-line no-console
      console.error('[GlobalSearch][inventory] sub-query rejected:', s.reason);
      return [];
    }
  });
  if (rows.length === 0 && hadRejection) {
    throw new Error('Inventory search source failed');
  }
  const unique = Array.from(new Map(rows.map(r => [r.id, r])).values()).slice(0, limit);

  return unique.map(dbItem => ({
    id: dbItem.id,
    type: 'inventory' as const,
    title: `${dbItem.name}`,
    subtitle: dbItem.sku ? `SKU: ${dbItem.sku}` : (dbItem.status || ''),
    price: dbItem.daily_price,
    description: `${dbItem.daily_price || 0} ₽/день`,
    icon: 'Package',
    url: `/inventory`,
    relevance: calculateRelevance(query, [dbItem.name, dbItem.description, dbItem.sku || ''], queryType),
    createdAt: dbItem.created_at,
    status: dbItem.status
  }));
};
